/**
 * Simulator Content Component
 * Componente reutilizável que contém toda a lógica de simulação
 * Pode ser usado tanto no modal quanto na página principal
 */

import { useState, useMemo } from 'react';
import { formatBRL as formatCurrency } from '@/lib/format';

/**
 * Percentual do jeito que se lê em voz alta: "30%", "37,5%" — sem o ",0" que
 * o toFixed(1) força e sem a vírgula virar ponto. Os percentuais de lance são
 * quase sempre inteiros; mostrar "30,0%" ao lado de um valor em reais só
 * carrega a linha.
 */
const formatPct = (n: number): string => {
  const arredondado = Math.round(n * 10) / 10;
  return `${String(arredondado).replace('.', ',')}%`;
};
import { calcParcelaFlex } from './parcela-flex';
import { computeSimulacao } from './simulacao';
import { PercentStepper } from './PercentStepper';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface SimulatorContentProps {
  valorCredito: number;
  parcelaSemSeguro: number;
  valorSeguro: number;
  prazo: number;
  taxaAdmin: number;
  isFlex: boolean;
  isAuto: boolean;
  tipoSeguro?: string | null;
  // Taxa de seguro mensal sobre saldo devedor, em formato percentual (ex: 0.04 = 0.04%)
  taxaSeguro?: number | null;
  fixedLancePercent?: number;
  showGerarProposta?: boolean;
  onGerarProposta?: (data: any) => Promise<void>;
}

/**
 * Percentuais do Lance Fidelidade, do maior pro menor.
 *
 * São fixos por definição da administradora — não é uma faixa que o vendedor
 * ajusta. Por isso a barra percorre ÍNDICES desta lista: ela não consegue parar
 * num valor que não existe como produto.
 */
const PERCENTUAIS_FIDELIDADE = [30, 20, 15, 10] as const;

export const SimulatorContent = ({
  valorCredito,
  parcelaSemSeguro,
  valorSeguro,
  prazo,
  taxaAdmin,
  isFlex,
  isAuto,
  tipoSeguro,
  taxaSeguro,
  fixedLancePercent = 30,
  showGerarProposta = false,
  onGerarProposta,
}: SimulatorContentProps) => {
  const [seguro, setSeguro] = useState<string>('Não');
  const tipoSeguroNorm = (tipoSeguro || '').toUpperCase();
  const seguroObrigatorio = tipoSeguroNorm === 'SEGURO OBRIGATORIO';
  const seguroForcadoPos = seguroObrigatorio || tipoSeguroNorm === 'SEGURO NA CONTEMPLACAO';
  // Quando o grupo exige seguro, ignora o toggle do usuário (parcela pré sempre com seguro)
  const seguroEfetivo = seguroObrigatorio ? 'Sim' : seguro;
  const [juncaoCota, setJuncaoCota] = useState<string>('1');
  const [lanceLivre, setLanceLivre] = useState<string>('40');
  const [lanceEmbutido, setLanceEmbutido] = useState<string>('30');
  const [lanceFixoAtivo, setLanceFixoAtivo] = useState<boolean>(false);
  // Lance Fidelidade: percentuais FIXOS definidos pela administradora, não uma
  // faixa contínua. A barra anda por índice e não por valor — assim ela não tem
  // como parar em 22%, que não existe como produto.
  const [lanceFidelidadeAtivo, setLanceFidelidadeAtivo] = useState<boolean>(false);
  const [indiceFidelidade, setIndiceFidelidade] = useState<number>(0);
  const percFidelidade = PERCENTUAIS_FIDELIDADE[indiceFidelidade];
  const [lanceLivreAtivo, setLanceLivreAtivo] = useState<boolean>(false);
  const [descontoFlex, setDescontoFlex] = useState<string>('0');
  const [mesesContemplacao, setMesesContemplacao] = useState<number>(1);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
  
  const [tempMesesContemplacao, setTempMesesContemplacao] = useState<number>(1);

  const handleMesesContemplacaoRelease = () => {
    setMesesContemplacao(tempMesesContemplacao);
  };

  // Livre + embutido nunca passam de 100%: mexer num ajusta o outro, como os
  // sliders já faziam. O campo entrega o valor já limitado (0–100 / 0–30).
  const mudarLanceLivre = (valor: number) => {
    setLanceLivre(valor.toString());
    const embutido = parseFloat(lanceEmbutido) || 0;
    if (valor + embutido > 100) setLanceEmbutido(Math.max(0, 100 - valor).toString());
  };

  const mudarLanceEmbutido = (valor: number) => {
    setLanceEmbutido(valor.toString());
    const livre = parseFloat(lanceLivre) || 0;
    if (livre + valor > 100) setLanceLivre(Math.max(0, 100 - valor).toString());
  };

  // ============================================
  // CÁLCULOS BASEADOS NO EXCEL SERVOPA
  // ============================================
  
  const calculos = useMemo(
    () =>
      computeSimulacao({
        valorCredito, parcelaSemSeguro, valorSeguro, prazo, taxaAdmin,
        mesesContemplacao, juncaoCota, lanceLivre, lanceEmbutido,
        fixedLancePercent, descontoFlex, isFlex, isAuto,
        // O percentual escolhido na barra, SEMPRE — o "Ativo" não entra aqui.
        //
        // Zerar o percentual com o toggle desligado deixava o card mostrando
        // "Lance fidelidade (30%)" no rótulo e R$ 0,00 no valor, com crédito e
        // saldo iguais aos do Sorteio: o rótulo lia a barra e o número era
        // calculado com zero. Pior ao lado do Lance Fixo, que simula do mesmo
        // jeito com o toggle igualmente desligado — dava a impressão de conta
        // errada, quando a conta nunca tinha sido feita.
        //
        // Os três "Ativo" escolhem quais cenários entram na PROPOSTA gerada
        // (ver o payload do Gerar Proposta), não o que a tela simula.
        percLanceFidelidade: percFidelidade,
        seguroEfetivo, seguroForcadoPos, taxaSeguro,
      }),
    [lanceLivre, lanceEmbutido, juncaoCota, seguroEfetivo, seguroForcadoPos, descontoFlex, mesesContemplacao, valorCredito, parcelaSemSeguro, valorSeguro, prazo, taxaAdmin, isFlex, isAuto, taxaSeguro, fixedLancePercent, percFidelidade],
  );

  const getParcelaComFlex = (desconto: number) =>
    calcParcelaFlex({ parcelaSemSeguro, valorSeguro, hasInsurance: seguroEfetivo === 'Sim' }, desconto);

  const parcelaBaseDisplay = parcelaSemSeguro + (seguroEfetivo === 'Sim' ? valorSeguro : 0);

  return (
    <div className="space-y-6">
      {/* Parcela */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Parcela
            {isFlex && <span className="text-xs bg-green-500 text-white px-3 py-1 rounded-full font-bold">FLEX</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Seguro */}
            <div>
              <Label htmlFor="seguro">Seguro</Label>
              <Select value={seguroEfetivo} onValueChange={setSeguro} disabled={seguroObrigatorio}>
                <SelectTrigger id="seguro" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sim">Sim</SelectItem>
                  <SelectItem value="Não">Não</SelectItem>
                </SelectContent>
              </Select>
              {seguroObrigatorio && (
                <p className="text-xs text-muted-foreground mt-1">Seguro obrigatório neste grupo</p>
              )}
            </div>

            {/* Parcela */}
            <div>
              <Label htmlFor="parcela">Parcela</Label>
              {isFlex ? (
                <Select value={descontoFlex} onValueChange={setDescontoFlex}>
                  <SelectTrigger id="parcela" className="mt-1">
                    <SelectValue placeholder="Selecione desconto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{formatCurrency(parcelaBaseDisplay)} - Normal - {seguroEfetivo === 'Sim' ? 'Com' : 'Sem'} seguro</SelectItem>
                    <SelectItem value="10">{formatCurrency(getParcelaComFlex(10))} - Flex -10% - {seguroEfetivo === 'Sim' ? 'Com' : 'Sem'} seguro</SelectItem>
                    <SelectItem value="20">{formatCurrency(getParcelaComFlex(20))} - Flex -20% - {seguroEfetivo === 'Sim' ? 'Com' : 'Sem'} seguro</SelectItem>
                    <SelectItem value="30">{formatCurrency(getParcelaComFlex(30))} - Flex -30% - {seguroEfetivo === 'Sim' ? 'Com' : 'Sem'} seguro</SelectItem>
                    <SelectItem value="40">{formatCurrency(getParcelaComFlex(40))} - Flex -40% - {seguroEfetivo === 'Sim' ? 'Com' : 'Sem'} seguro</SelectItem>
                    <SelectItem value="50">{formatCurrency(getParcelaComFlex(50))} - Flex -50% - {seguroEfetivo === 'Sim' ? 'Com' : 'Sem'} seguro</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="mt-1 border rounded-md px-3 py-2 bg-muted">
                  <div className="text-sm font-semibold">{formatCurrency(parcelaBaseDisplay)}</div>
                  <div className="text-xs text-muted-foreground">
                    {seguroEfetivo === 'Sim' ? 'Normal - Com seguro' : 'Normal - Sem seguro'}
                  </div>
                </div>
              )}
            </div>

            {/* Junção de cota */}
            <div>
              <Label htmlFor="juncaoCota">Junção de cotas</Label>
              <Select value={juncaoCota} onValueChange={setJuncaoCota}>
                <SelectTrigger id="juncaoCota" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 100 }, (_, i) => i + 1).map((num) => (
                    <SelectItem key={num} value={String(num)}>
                      {num}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estilo do polegar dos sliders que ficaram (meses de contemplação). */}
      <style>{`
                  input[type="range"]::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: hsl(var(--primary));
                    cursor: grab;
                    border: 3px solid white;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                  }
                  input[type="range"]:active::-webkit-slider-thumb {
                    cursor: grabbing;
                  }
                  input[type="range"]::-moz-range-thumb {
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: hsl(var(--primary));
                    cursor: grab;
                    border: 3px solid white;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                  }
                  input[type="range"]:active::-moz-range-thumb {
                    cursor: grabbing;
                  }
                `}</style>

      {/* Lance Livre */}
      <Card>
        <CardHeader>
          <CardTitle>Lance livre</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Lance Livre: digitado em %, com − e + (no lugar do slider). */}
            <PercentStepper
              id="percLanceLivre"
              label="Lance livre"
              valor={parseFloat(lanceLivre) || 0}
              max={100}
              onChange={mudarLanceLivre}
            >
              <p className="mt-1.5 text-xs text-muted-foreground">
                Valor a ser pago: <b className="text-foreground">{formatCurrency(calculos.valorLanceLivre)}</b>
              </p>
              {/* ── POR QUE O VALOR NÃO ACOMPANHA CADA 1% ────────────────────
                  Um especialista reportou "coloquei 37%, veio R$ 16.656; mudei
                  pra 36% e não mudou nada" e concluiu que a conta estava
                  errada. Não estava: 37% dava 18,49 parcelas e 36% dava 18,00,
                  e as duas arredondam pra 18 — a Servopa oferta o lance em
                  parcelas inteiras.

                  A regra sempre esteve no cálculo; o que faltava era a tela
                  dizer. Sem isso o valor parece aleatório, e quem está na
                  frente do cliente não tem como defender o número. */}
              {calculos.parcelasLanceOfertado > 0 && (
                <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
                  Arredondado para <b>{calculos.parcelasLanceOfertado} parcela
                  {calculos.parcelasLanceOfertado === 1 ? '' : 's'}</b> de{' '}
                  {formatCurrency(calculos.parcelaPrincipalPos)} — a Servopa oferta o lance em
                  parcelas inteiras; o valor muda a cada{' '}
                  {calculos.passoLancePct.toFixed(1).replace('.', ',')}%, aproximadamente.
                </p>
              )}
            </PercentStepper>

            {/* Lance Fidelidade: só os percentuais que existem como produto. */}
            <div>
              <span id="percLanceFidelidade" className="mb-2 block text-sm font-medium">
                Lance fidelidade
              </span>
              <div role="group" aria-labelledby="percLanceFidelidade" className="grid grid-cols-4 gap-2">
                {PERCENTUAIS_FIDELIDADE.map((p, i) => (
                  <Button
                    key={p}
                    type="button"
                    variant={i === indiceFidelidade ? 'default' : 'outline'}
                    aria-pressed={i === indiceFidelidade}
                    onClick={() => setIndiceFidelidade(i)}
                  >
                    {p}%
                  </Button>
                ))}
              </div>
            </div>

            {/* Lance Embutido: mesmo campo do Livre, até 30%. */}
            <PercentStepper
              id="percLanceEmbutido"
              label="Lance embutido"
              valor={parseFloat(lanceEmbutido) || 0}
              max={30}
              onChange={mudarLanceEmbutido}
            >
              <p className="mt-1.5 text-xs text-muted-foreground">
                Valor a ser deduzido da carta de crédito:{' '}
                <b className="text-foreground">{formatCurrency(calculos.valorLanceEmbutido)}</b>
              </p>
              {calculos.parcelasLanceEmbutido > 0 && (
                <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
                  Arredondado para <b>{calculos.parcelasLanceEmbutido} parcela
                  {calculos.parcelasLanceEmbutido === 1 ? '' : 's'}</b> de{' '}
                  {formatCurrency(calculos.parcelaPrincipalPos)} — a Servopa oferta o lance em
                  parcelas inteiras.
                </p>
              )}
            </PercentStepper>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <p className="text-sm">
              <strong>Lance total:</strong> {formatCurrency(calculos.valorLanceLivre + calculos.valorLanceEmbutido)} (
              {(parseFloat(lanceLivre) + parseFloat(lanceEmbutido)).toFixed(2)}%)
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Lance livre é pago à vista na contemplação. Lance embutido é diluído nas parcelas e reduz o crédito disponível. 
              <strong className="block mt-1">A soma dos lances é limitada a 100%.</strong>
            </p>
          </div>
          
          {/* Slider para Mês de Contemplação */}
          <div className="mt-4">
            <Label htmlFor="mesContemplacao" className="flex items-center justify-between">
              <span>Mês de contemplação</span>
              <span className="text-primary font-semibold">{tempMesesContemplacao}º mês</span>
            </Label>
            <div className="relative p-1 bg-gray-200 dark:bg-gray-700 rounded-lg mt-2">
              <input
                id="mesContemplacao"
                type="range"
                min="1"
                max={prazo}
                value={tempMesesContemplacao}
                onChange={(e) => setTempMesesContemplacao(parseInt(e.target.value))}
                onMouseUp={handleMesesContemplacaoRelease}
                onTouchEnd={handleMesesContemplacaoRelease}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${((tempMesesContemplacao - 1) / (prazo - 1)) * 100}%, #e5e7eb ${((tempMesesContemplacao - 1) / (prazo - 1)) * 100}%, #e5e7eb 100%)`,
                  WebkitAppearance: 'none'
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1º mês</span>
              <span>{prazo}º mês</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pré Contemplação */}
      <Card>
        <CardHeader>
          <CardTitle>Pré-contemplação</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Crédito contratado</span>
              <span className="font-semibold">{formatCurrency(calculos.valorCreditoJunto)}</span>
            </div>
            <div className="flex justify-between">
              <span>Parcela</span>
              <span className="font-semibold">{formatCurrency(calculos.parcelaJunta)}</span>
            </div>
            <div className="flex justify-between">
              <span>Prazo</span>
              <span className="font-semibold">{prazo}m</span>
            </div>
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between">
                <span>Mês de contemplação</span>
                <span className="font-semibold">{mesesContemplacao}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pré Contemplação - 3 Cards */}
      <div>
        <h3 className="text-xl font-semibold mb-4">Pré-contemplação</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Sorteio */}
          <Card>
            <CardHeader>
              <CardTitle>Sorteio</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Crédito liberado</span>
                <span className="font-semibold">{formatCurrency(calculos.creditoSorteio)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-muted-foreground">Saldo devedor:</span>
                <span className="font-semibold">{formatCurrency(calculos.saldoDevedorSorteio)}</span>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs text-muted-foreground mb-2">
                  Se contemplado no {mesesContemplacao}º mês
                </div>
                <div className="text-sm font-semibold">
                  {calculos.seguroSorteio > 0
                    ? <>{calculos.parcelasRestantesSorteio}x de {formatCurrency(calculos.parcelaAPagarSorteio - calculos.seguroSorteio)} + {formatCurrency(calculos.seguroSorteio)} <span className="font-normal text-muted-foreground">(seguro)</span> = {formatCurrency(calculos.parcelaAPagarSorteio)}</>
                    : <>{calculos.parcelasRestantesSorteio}x de {formatCurrency(calculos.parcelaAPagarSorteio)}</>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lance Fixo */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Lance fixo</CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Ativo</span>
                  <button
                    onClick={() => setLanceFixoAtivo(!lanceFixoAtivo)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      lanceFixoAtivo ? 'bg-[#354660]' : 'bg-gray-300'
                    }`}
                    aria-label="Ativar lance fixo"
                  >
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                      lanceFixoAtivo ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Crédito liberado</span>
                <span className="font-semibold">{formatCurrency(calculos.creditoLanceFixo)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lance fixo ({calculos.lanceFixoPercent.toFixed(0)}%)</span>
                <span className="font-semibold">{formatCurrency(calculos.valorLanceFixo)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-muted-foreground">Saldo devedor:</span>
                <span className="font-semibold">{formatCurrency(calculos.saldoDevedorLanceFixo)}</span>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs text-muted-foreground mb-2">
                  Se contemplado no {mesesContemplacao}º mês
                </div>
                <div className="text-sm font-semibold">
                  {calculos.seguroLanceFixo > 0
                    ? <>{calculos.parcelasRestantesLanceFixo}x de {formatCurrency(calculos.parcelaAPagarLanceFixo - calculos.seguroLanceFixo)} + {formatCurrency(calculos.seguroLanceFixo)} <span className="font-normal text-muted-foreground">(seguro)</span> = {formatCurrency(calculos.parcelaAPagarLanceFixo)}</>
                    : <>{calculos.parcelasRestantesLanceFixo}x de {formatCurrency(calculos.parcelaAPagarLanceFixo)}</>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lance Fidelidade — mesmo desenho do Fixo, porque é a mesma conta:
              embutido, abate saldo e prazo. O que muda é de onde vem o
              percentual. */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Lance fidelidade</CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Ativo</span>
                  <button
                    onClick={() => setLanceFidelidadeAtivo(!lanceFidelidadeAtivo)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      lanceFidelidadeAtivo ? 'bg-[#354660]' : 'bg-gray-300'
                    }`}
                    aria-label="Ativar lance fidelidade"
                  >
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                      lanceFidelidadeAtivo ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Crédito liberado</span>
                <span className="font-semibold">{formatCurrency(calculos.creditoLanceFidelidade)}</span>
              </div>
              <div className="flex justify-between">
                {/* O percentual sai do RESULTADO, não do estado da barra: assim
                    rótulo e valor não têm como discordar de novo. */}
                <span className="text-muted-foreground">Lance fidelidade ({calculos.lanceFidelidadePercent}%)</span>
                <span className="font-semibold">{formatCurrency(calculos.valorLanceFidelidade)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-muted-foreground">Saldo devedor:</span>
                <span className="font-semibold">{formatCurrency(calculos.saldoDevedorLanceFidelidade)}</span>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs text-muted-foreground mb-2">
                  Se contemplado no {mesesContemplacao}º mês
                </div>
                <div className="text-sm font-semibold">
                  {calculos.seguroLanceFidelidade > 0
                    ? <>{calculos.parcelasRestantesLanceFidelidade}x de {formatCurrency(calculos.parcelaAPagarLanceFidelidade - calculos.seguroLanceFidelidade)} + {formatCurrency(calculos.seguroLanceFidelidade)} <span className="font-normal text-muted-foreground">(seguro)</span> = {formatCurrency(calculos.parcelaAPagarLanceFidelidade)}</>
                    : <>{calculos.parcelasRestantesLanceFidelidade}x de {formatCurrency(calculos.parcelaAPagarLanceFidelidade)}</>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lance Livre */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Lance livre</CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Ativo</span>
                  <button
                    onClick={() => setLanceLivreAtivo(!lanceLivreAtivo)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      lanceLivreAtivo ? 'bg-[#354660]' : 'bg-gray-300'
                    }`}
                    aria-label="Ativar lance livre"
                  >
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                      lanceLivreAtivo ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Crédito liberado</span>
                <span className="font-semibold">{formatCurrency(calculos.creditoLanceLivre)}</span>
              </div>
              {/* Os percentuais saem do RESULTADO, como já era em Fixo e
                  Fidelidade. Sem eles o card virava adivinhação: "Lance
                  embutido" dava o mesmo valor do Lance Fixo (os dois são 30%
                  do embutido do grupo) e parecia que o simulador tinha
                  ignorado os 37% escolhidos — que estavam aplicados no "Lance
                  pago", só que sem rótulo dizendo isso. */}
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Lance embutido ({formatPct(calculos.lanceEmbutidoPercent)})
                </span>
                <span className="font-semibold">{formatCurrency(calculos.valorLanceEmbutido)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Lance pago ({formatPct(calculos.lanceLivrePercent)})
                </span>
                <span className="font-semibold">{formatCurrency(calculos.valorLanceLivre)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">
                  Ofertado ({formatPct(calculos.lanceOfertadoPercent)})
                </span>
                <span className="text-muted-foreground">
                  {formatCurrency(calculos.valorLanceEmbutido + calculos.valorLanceLivre)}
                  {calculos.parcelasLanceOfertado > 0
                    ? ` · ${calculos.parcelasLanceOfertado} parcelas`
                    : ''}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-muted-foreground">Saldo devedor:</span>
                <span className="font-semibold">{formatCurrency(calculos.saldoDevedorLanceLivre)}</span>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs text-muted-foreground mb-2">
                  Se contemplado no {mesesContemplacao}º mês
                </div>
                <div className="text-sm font-semibold">
                  {calculos.seguroLanceLivre > 0
                    ? <>{calculos.parcelasRestantesLanceLivre}x de {formatCurrency(calculos.parcelaAPagar - calculos.seguroLanceLivre)} + {formatCurrency(calculos.seguroLanceLivre)} <span className="font-normal text-muted-foreground">(seguro)</span> = {formatCurrency(calculos.parcelaAPagar)}</>
                    : <>{calculos.parcelasRestantesLanceLivre}x de {formatCurrency(calculos.parcelaAPagar)}</>}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Botão Gerar Proposta */}
      {showGerarProposta && (
        <div className="flex justify-end">
          <Button 
            size="lg" 
            disabled={isGeneratingPdf}
            className="px-8 bg-[#354660] text-white hover:bg-[#29374B] dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
            onClick={async () => {
              setIsGeneratingPdf(true);
              try {
                await onGerarProposta?.({
                  seguro,
                  juncaoCota,
                  lanceLivre,
                  lanceEmbutido,
                  descontoFlex,
                  mesesContemplacao,
                  valorCredito,
                  lanceFixoAtivo,
                  lanceLivreAtivo,
                  lanceFidelidadeAtivo,
                  ...calculos,
                });
              } finally {
                setIsGeneratingPdf(false);
              }
            }}
          >
            {isGeneratingPdf && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isGeneratingPdf ? 'Gerando proposta...' : 'Gerar proposta'}
          </Button>
        </div>
      )}
    </div>
  );
};
